'use client';

import i18next from 'i18next';
import { t } from 'i18next';
import { z as zod } from 'zod';

import { env } from 'src/global-config';

const required = zod.string().min(1, { message: t('required') });
const optional = zod.string().optional();
const optionalNumber = zod
  .string()
  .optional()
  .refine(
    (val) => {
      if (val === undefined || val === '') return true; // allow undefined or empty only
      const trimmed = val.trim();
      if (trimmed === '') return false; // reject whitespace-only strings
      const num = Number(trimmed);
      return !isNaN(num) && num > 0;
    },
    {
      message: t('invalid'),
    }
  );
const emailValidation = zod
  .string()
  .min(1, { message: t('required') })
  .email(t('Enter_Valid_EmailId'));
const mobileNumberValidation = zod
  .string()
  .min(1, { message: t('required') })
  .regex(/^\d+$/, t('mobile_number_must_contain_numbers'))
  .refine(
    (val) => {
      const isSaudi =
        /^05\d{8}$/.test(val) || // local format
        /^5\d{8}$/.test(val) || // without 0
        /^9665\d{8}$/.test(val); // with country code

      const isIndian = /^91\d{10}$/.test(val); // Indian format (+91 + 10 digits)

      if (env === 'qa' || env === 'dev') {
        return isSaudi || isIndian;
      }
      return isSaudi;
    },
    { message: t('invalid_phoneNumber') }
  );

const OTPValidation = zod
  .string()
  .min(1, 'OTP is required')
  .max(4, 'OTP must be at most 4 digits')
  .regex(/^\d+$/, 'OTP must contain only numbers');

const numberValidation = zod.string().min(1, 'required').regex(/^\d+$/, 'Only Numbers are allowed');

const zipCodeValidation = zod.string().optional();
// .refine((val) => !val || /^\d+$/.test(val), {
//   message: 'Only numbers are allowed',
// });

// This allows '', null, or object — but only objects with at least 1 key will pass validation
// const dynamicObject = zod
//   .union([zod.string(), zod.null(), zod.record(zod.any())])
//   .refine((val) => typeof val === 'object' && val !== null && Object.keys(val).length > 0, {
//     message: t('required'),
//   });

// const dynamicObject = zod.union([
//   zod.string().min(1, t('required')), // non-empty string
//   zod.null(), // allow null
//   zod.object({}).passthrough(), // any object allowed
// ]);

const dynamicObject = zod.union([
  zod.string().min(1, t('required')), // non-empty string
  zod
    .object({})
    .passthrough()
    .refine(
      (obj) => Object.keys(obj).length > 0,
      { message: t('required') } // non-empty object
    ),
]);

export const saveLocationSchema = zod.object({
  name: required,
});

export const SignUpSchema = zod.object({
  companyName: required,
  companyEmail: emailValidation,
  vatNumber: required,
  city: required,
  zipCode: required,
  address: required,
  responsiblePersonName: required,
});

export const SignInSchema = zod.object({
  mobileNumber: mobileNumberValidation,
});

export const OTPSchema = zod.object({
  otp: OTPValidation,
});

export const ProfileSchema = zod.object({
  companyName: required,
  companyEmail: emailValidation,
  address: optional,
  city: optional,
  zipCode: zipCodeValidation,
  vatNumber: numberValidation,
  responsiblePersonName: required,
  // mobileNumber: mobileNumberValidation,
});

export const ProfilePicSchema = zod.object({
  avatar: zod
    .instanceof(File, { message: 'File is required' }) // fallback if null or not File
    // .refine((file) => file.size > 0, 'File is required') // explicit size > 0 check
    .refine((file) => file.size <= 3 * 1024 * 1024, 'Max file size is 3MB')
    .refine(
      (file) => ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type),
      'Invalid file type'
    ),
});

export const AddNewUserSchema = zod.object({
  userName: required,
  email: optional,
  designation: optional,
  roleInCompany: dynamicObject,
  storeLocation: optional,
  storeName: optional,
  mobileNumber: mobileNumberValidation,
});

export const updateContactPersonSchema = zod.object({
  pickupLocation: mobileNumberValidation,
  deliveryLocation1: mobileNumberValidation,
  deliveryLocation2: zod
    .string()
    .optional()
    .refine((val) => !val || /^\d{10,12}$/.test(val), {
      message: 'mobile number must be 10 to 12 digits',
    }),
});
export const updateContactPersonSchema_ORDER = zod.object({
  pickupLocation: mobileNumberValidation,
  deliveryLocation1: mobileNumberValidation,
  deliveryLocation2: zod
    .string()
    .optional()
    .refine((val) => !val || /^\d{10,12}$/.test(val), {
      message: 'mobile number must be 10 to 12 digits',
    }),
  note: zod.string().optional(),
});

// export const bookingSchema = zod
//   .object({
//     pickupLocation_search: dynamicObject,
//     deliveryLocation_1_search: dynamicObject,
//     deliveryLocation_2_search: zod
//       .union([zod.string(), zod.null(), zod.record(zod.any())])
//       .optional(), // will be refined conditionally
//     truckSize: dynamicObject,
//     trucktype: dynamicObject,
//     quantity: zod.number().min(1, 'Quantity must be at least 1'),
//     timeAndSlot: dynamicObject,
//     isSecondDropLocationVisible: zod.boolean(),
//     goodsValue: optionalNumber,
//     loadType: dynamicObject,
//     loadQauntity: optionalNumber,
//     loadTypeText: optional,
//   })
//   .superRefine((data, ctx) => {
//     console.log('🚀 super refine data', data);
//     const isValidObject = (val) =>
//       typeof val === 'object' && val !== null && Object.keys(val).length > 0;

//     // ✅ If second drop is visible, ensure it's valid
//     if (data.isSecondDropLocationVisible && !isValidObject(data.deliveryLocation_2_search)) {
//       ctx.addIssue({
//         path: ['deliveryLocation_2_search'],
//         code: zod.ZodIssueCode.custom,
//         message: t('required'),
//       });
//     }

//     // ✅ loadQauntity required if packetPrice > 0
//     if (
//       isValidObject(data.loadType) &&
//       typeof data.loadType.packetPrice === 'number' &&
//       data.loadType.packetPrice > 0
//     ) {
//       if (!data.loadQauntity || Number(data.loadQauntity) <= 0) {
//         ctx.addIssue({
//           path: ['loadQauntity'],
//           code: zod.ZodIssueCode.custom,
//           message: t('required'),
//         });
//       }
//     }

//     // ✅ loadTypeText required if label === 'others'
//     if (
//       isValidObject(data.loadType) &&
//       data.loadType.label?.toLowerCase() === 'others' &&
//       !data.loadTypeText?.trim()
//     ) {
//       ctx.addIssue({
//         path: ['loadTypeText'],
//         code: zod.ZodIssueCode.custom,
//         message: t('required'),
//       });
//     }

//     // ============================
//     // 🚨 Same location validations
//     // ============================
//     const getPlaceId = (val) => (isValidObject(val) ? val.place_id : null);

//     const pickupId = getPlaceId(data.pickupLocation_search);
//     const delivery1Id = getPlaceId(data.deliveryLocation_1_search);
//     const delivery2Id = getPlaceId(data.deliveryLocation_2_search);

//     // Pickup vs Delivery 1
//     if (pickupId && delivery1Id && pickupId === delivery1Id) {
//       ctx.addIssue({
//         path: ['deliveryLocation_1_search'],
//         code: zod.ZodIssueCode.custom,
//         message: t('pickupAndDropLocationCannotBeSame'),
//       });
//     }

//     // Pickup vs Delivery 2
//     // if (pickupId && delivery2Id && pickupId === delivery2Id) {
//     //   ctx.addIssue({
//     //     path: ['deliveryLocation_2_search'],
//     //     code: zod.ZodIssueCode.custom,
//     //     message: t('pickupAndDeliverySame'),
//     //   });
//     // }

//     // Delivery 1 vs Delivery 2
//     if (delivery1Id && delivery2Id && delivery1Id === delivery2Id) {
//       ctx.addIssue({
//         path: ['deliveryLocation_2_search'],
//         code: zod.ZodIssueCode.custom,
//         message: t('pickupAndDropLocationCannotBeSame'),
//       });
//     }
//   });

export const bookingSchema = zod
  .object({
    pickupLocation_search: dynamicObject,
    deliveryLocation_1_search: dynamicObject,
    deliveryLocation_2_search: dynamicObject.optional().nullable(), // will be refined conditionally
    truckSize: dynamicObject,
    trucktype: dynamicObject,
    quantity: zod.number().min(1, 'Quantity must be at least 1'),
    timeAndSlot: dynamicObject,
    isSecondDropLocationVisible: zod.boolean(),
    goodsValue: optionalNumber,
    loadType: dynamicObject,
    loadQauntity: optionalNumber,
    loadTypeText: optional,
  })
  .superRefine((data, ctx) => {
    const isValidObject = (val) =>
      typeof val === 'object' && val !== null && Object.keys(val).length > 0;

    // ✅ If second drop is visible, ensure it's valid
    if (data.isSecondDropLocationVisible && !isValidObject(data.deliveryLocation_2_search)) {
      ctx.addIssue({
        path: ['deliveryLocation_2_search'],
        code: zod.ZodIssueCode.custom,
        message: t('required'),
      });
    }

    // ✅ loadQauntity required if packetPrice > 0
    if (
      isValidObject(data.loadType) &&
      typeof data.loadType.packetPrice === 'number' &&
      data.loadType.packetPrice > 0
    ) {
      if (!data.loadQauntity || Number(data.loadQauntity) <= 0) {
        ctx.addIssue({
          path: ['loadQauntity'],
          code: zod.ZodIssueCode.custom,
          message: t('required'),
        });
      }
    }

    // ✅ loadTypeText required if label === 'others'
    if (
      isValidObject(data.loadType) &&
      data.loadType.label?.toLowerCase() === 'others' &&
      !data.loadTypeText?.trim()
    ) {
      ctx.addIssue({
        path: ['loadTypeText'],
        code: zod.ZodIssueCode.custom,
        message: t('required'),
      });
    }

    // ============================
    // 🚨 Same location validations
    // ============================
    const getPlaceId = (val) => (isValidObject(val) ? val.place_id : null);

    const pickupId = getPlaceId(data.pickupLocation_search);
    const delivery1Id = getPlaceId(data.deliveryLocation_1_search);
    const delivery2Id = getPlaceId(data.deliveryLocation_2_search);

    // Pickup vs Delivery 1
    if (pickupId && delivery1Id && pickupId === delivery1Id) {
      ctx.addIssue({
        path: ['deliveryLocation_1_search'],
        code: zod.ZodIssueCode.custom,
        message: t('pickupAndDropLocationCannotBeSame'),
      });
    }

    // Pickup vs Delivery 2
    // if (pickupId && delivery2Id && pickupId === delivery2Id) {
    //   ctx.addIssue({
    //     path: ['deliveryLocation_2_search'],
    //     code: zod.ZodIssueCode.custom,
    //     message: t('pickupAndDeliverySame'),
    //   });
    // }

    // Delivery 1 vs Delivery 2
    if (delivery1Id && delivery2Id && delivery1Id === delivery2Id) {
      ctx.addIssue({
        path: ['deliveryLocation_2_search'],
        code: zod.ZodIssueCode.custom,
        message: t('pickupAndDropLocationCannotBeSame'),
      });
    }
  });

export const WalletAddMoneySchema = zod.object({
  amount: optionalNumber,
});

export const favoriteOrderSchema = zod
  .object({
    quantity: zod.number().min(1, 'Quantity must be at least 1'),
    timeAndSlot: dynamicObject,
    goodsValue: optionalNumber,
    loadType: dynamicObject,
    loadQauntity: optionalNumber,
    loadTypeText: optional,
  })
  .superRefine((data, ctx) => {
    const isValidObject = (val) =>
      typeof val === 'object' && val !== null && Object.keys(val).length > 0;

    // ✅ loadQauntity required if packetPrice > 0
    if (
      isValidObject(data?.loadType) &&
      typeof data?.loadType?.packetPrice === 'number' &&
      data?.loadType?.packetPrice > 0
    ) {
      if (!data?.loadQauntity || Number(data?.loadQauntity) <= 0) {
        ctx.addIssue({
          path: ['loadQauntity'],
          code: zod.ZodIssueCode.custom,
          message: t('required'),
        });
      }
    }

    // ✅ loadTypeText required if label === 'others'
    if (
      isValidObject(data?.loadType) &&
      data?.loadType?.label?.toLowerCase() === 'others' &&
      !data?.loadTypeText?.trim()
    ) {
      ctx.addIssue({
        path: ['loadTypeText'],
        code: zod.ZodIssueCode.custom,
        message: t('required'),
      });
    }
  });

export const addFavoriteOrderSchema = zod.object({
  orderName: zod.string().min(1, 'name must be at least 1 character long'),
});

export const updateSavedLocationSchema = zod.object({
  name: zod.string().min(1, 'name must be at least 1 character long'),
});

export const googleAddressPasteSchema = zod.object({
  address: zod
    .string()
    .min(1, 'required')
    .superRefine((val, ctx) => {
      const isValidGoogleAddress = (input) => {
        const googleAddressRegex =
          /^(https?:\/\/)?(www\.)?(google\.com|maps\.google\.com|maps\.app\.goo\.gl)\/.*$/;
        return googleAddressRegex.test(input);
      };
      if (!isValidGoogleAddress(val)) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: t('Please Enter Google Maps URL only'),
        });
      }
    }),
});

export const userRegisterSchema = zod.object({
  name: required,
  email: emailValidation,
});

export const companyRegisterSchema = zod.object({
  companyName: required,
  companyEmail: emailValidation,
  vatNumber: numberValidation,
  responsiblePersonName: required,
});

export const serviceFormSchema = zod.object({
  company: required,
  mobile: mobileNumberValidation,
  service: dynamicObject,
  email: emailValidation,
});

export const TravelContactSchema = zod.object({
  name: required,
  mobileNumber: mobileNumberValidation,
  serviceNeeded: dynamicObject,
  email: emailValidation,
});

// ----------------------------------------------------------------------
